/**
 * index.js — /car-screen-wallpaper: обои И логотип загрузки для магнитолы.
 *
 * ── Что здесь делается и почему именно так ─────────────────────────────────
 * Экран магнитолы — не телефон и не монитор. У него разрешения, которых нет в
 * списках готовых размеров (1024×600, 1280×480, 1920×720, 2000×1200), и поверх
 * обоев ВСЕГДА лежит интерфейс: часы, строка значков, док. Обрезка «по центру»
 * раз за разом кладёт главное ровно под часы. Поэтому зоны интерфейса нарисованы
 * прямо на кадре — а с 15.09.2026 ещё и сам интерфейс в виде макета, и
 * измеренная читаемость белых значков на этом фоне.
 *
 * ── Логотип загрузки ───────────────────────────────────────────────────────
 * Спрос на него впятеро выше, чем на обои (Wordstat, РФ, 15.09.2026: «логотип
 * магнитолы» 4 380 и «заставка на магнитолу» 2 610 против «обои на магнитолу»
 * 740), а генератора нет ни одного: люди собирают файл в редакторе и потом
 * выясняют на форумах, что прошивке нужен 24-битный BMP ровно в разрешение
 * экрана. Режим «Логотип» делает именно этот файл — и знает, что браузер BMP не
 * кодирует (см. bmp.js).
 */

import * as M from './model.js';
import { encodeBmp24 } from './bmp.js';
import * as Boot from './bootanim.js';

const SLUG = 'car-screen-wallpaper';
const $ = (id) => document.getElementById(id);

let L = {};
const t = (k, sub) => {
    let s = L[k] != null ? L[k] : k;
    if (sub) for (const p in sub) s = String(s).split(':' + p).join(sub[p]);
    return s;
};
const stage = (n, d) => { try { window.__stage && window.__stage(n, d || {}); } catch (e) {} };
const ae = (ev, d) => { try { window.__ae && window.__ae(ev, Object.assign({ slug: SLUG }, d || {})); } catch (e) {} };
const toast = (m, type) => { try { window.__toast && window.__toast(m, type); } catch (e) {} };
const settings = {
    load: (k, f) => { try { return window.__toolSettings ? window.__toolSettings.load(SLUG, k, f) : f; } catch (e) { return f; } },
    save: (k, v) => { try { window.__toolSettings && window.__toolSettings.save(SLUG, k, v); } catch (e) {} },
};

/* ────────────────────────────────── состояние ─────────────────────────────── */

const S = {
    mode: 'wall',                 // 'wall' | 'logo'
    device: 'd:android-600',      // значение селекта
    screen: M.screenById('1024x600'),
    img: null, srcFile: null, limitKey: null,
    demo: null,                   // пример-картинка, пока фото не выбрано
    logo: null,                   // обработанный логотип (canvas)
    views: { wall: null, logo: null },
    darken: 30, blur: 0,
    overlay: 'zones',             // 'zones' | 'ui' | 'none'
    light: 'normal',              // 'normal' | 'day' | 'night'
    bg: '#000000', keyOut: false, tintWhite: false,
    text: '', textSize: 8, textColor: '#ffffff', spaced: true,
    fmt: { wall: 'jpg', logo: 'bmp' },
    busy: false,
    bootPreview: null,            // {start} — проигрывание появления в превью
};

const fitOf = (mode) => (mode === 'logo' ? 'logo' : 'cover');
const source = () => (S.mode === 'logo' ? S.logo : (S.img || S.demo));
const srcW = (src) => src.naturalWidth || src.width;
const srcH = (src) => src.naturalHeight || src.height;

function isPro() {
    try { if (window.__tierLimits && typeof window.__tierLimits.is_pro === 'boolean') return window.__tierLimits.is_pro; } catch (e) {}
    return false;
}

/* ───────────────────────────── пример и логотип ──────────────────────────── */

/**
 * Пример-кадр вместо пустой рамки: закат над дорогой. Он нужен не для красоты —
 * до выбора фото человек видит, ЧТО делает тул (зоны, макет, читаемость), и
 * может сначала выбрать магнитолу, как и написано в инструкции. В файл пример
 * не уходит: кнопка сохранения ждёт настоящее фото.
 */
function makeDemo() {
    const c = document.createElement('canvas');
    c.width = 1600; c.height = 1000;
    const g = c.getContext('2d');
    const sky = g.createLinearGradient(0, 0, 0, 640);
    sky.addColorStop(0, '#1e1b4b'); sky.addColorStop(0.45, '#7c3aed'); sky.addColorStop(0.8, '#f97316'); sky.addColorStop(1, '#fdba74');
    g.fillStyle = sky; g.fillRect(0, 0, 1600, 1000);
    g.fillStyle = 'rgba(255,237,213,.95)';
    g.beginPath(); g.arc(1040, 560, 120, 0, Math.PI * 2); g.fill();
    const hill = (pts, col) => { g.fillStyle = col; g.beginPath(); g.moveTo(0, 1000); pts.forEach(([x, y]) => g.lineTo(x, y)); g.lineTo(1600, 1000); g.fill(); };
    hill([[0, 600], [260, 470], [520, 590], [820, 430], [1120, 600], [1400, 500], [1600, 580]], '#4c1d95');
    hill([[0, 700], [300, 610], [640, 690], [980, 600], [1300, 680], [1600, 620]], '#2e1065');
    g.fillStyle = '#0f0a1f'; g.beginPath(); g.moveTo(0, 1000); g.lineTo(0, 760); g.lineTo(1600, 760); g.lineTo(1600, 1000); g.fill();
    g.fillStyle = '#1f1535'; g.beginPath(); g.moveTo(560, 1000); g.lineTo(780, 760); g.lineTo(820, 760); g.lineTo(1040, 1000); g.fill();
    g.strokeStyle = 'rgba(253,186,116,.8)'; g.lineWidth = 6; g.setLineDash([40, 36]);
    g.beginPath(); g.moveTo(800, 1000); g.lineTo(800, 770); g.stroke();
    return c;
}

/**
 * Логотип = исходник, приведённый к рабочему размеру, с убранным светлым полем
 * и/или перекрашенный в белый. Пересобирается только при смене исходника или
 * этих двух флажков — ползунки его не трогают.
 */
function rebuildLogo() {
    if (!S.img) { S.logo = null; return; }
    const iw = S.img.naturalWidth, ih = S.img.naturalHeight;
    const k = Math.min(1, 1600 / Math.max(iw, ih));
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(iw * k)); c.height = Math.max(1, Math.round(ih * k));
    const g = c.getContext('2d', { willReadFrequently: true });
    g.drawImage(S.img, 0, 0, c.width, c.height);
    if (S.keyOut || S.tintWhite) {
        const d = g.getImageData(0, 0, c.width, c.height);
        if (S.keyOut) M.keyOutCorners(d.data, c.width, c.height, 40);
        if (S.tintWhite) M.tint(d.data, [255, 255, 255]);
        g.putImageData(d, 0, 0);
    }
    S.logo = c;
}

/** Похоже ли поле картинки на сплошное светлое (типичный JPEG логотипа марки). */
function looksLikeLightField(im) {
    try {
        const c = document.createElement('canvas');
        c.width = 48; c.height = 48;
        const g = c.getContext('2d', { willReadFrequently: true });
        g.drawImage(im, 0, 0, 48, 48);
        const d = g.getImageData(0, 0, 48, 48).data;
        const at = (x, y) => (y * 48 + x) * 4;
        const pts = [at(0, 0), at(47, 0), at(0, 47), at(47, 47)];
        let min = 255;
        for (const i of pts) {
            if (d[i + 3] < 250) return false;
            min = Math.min(min, d[i], d[i + 1], d[i + 2]);
        }
        return min > 225;
    } catch (e) { return false; }
}

/* ─────────────────────────────────── вид ────────────────────────────────── */

function view() {
    const src = source();
    if (!src) return null;
    const fit = fitOf(S.mode);
    let v = S.views[S.mode];
    if (!v) {
        v = M.initialView(S.screen, srcW(src), srcH(src), fit, 0.45);
        if (S.mode === 'logo' && S.text.trim()) v.y -= S.screen.h * 0.06;
        S.views[S.mode] = v;
    }
    return v;
}

function setView(v) { S.views[S.mode] = v; }

function resetView() {
    S.views[S.mode] = null;
    view();
}

function setScreen(next) {
    if (!next || (S.screen && next.w === S.screen.w && next.h === S.screen.h)) { S.screen = next || S.screen; return; }
    const prev = S.screen;
    S.screen = next;
    for (const mode of ['wall', 'logo']) {
        const src = mode === 'logo' ? S.logo : (S.img || S.demo);
        const v = S.views[mode];
        if (src && v) S.views[mode] = M.retarget(v, prev, next, srcW(src), srcH(src), fitOf(mode));
    }
}

/* ────────────────────────────────── рисование ─────────────────────────────── */

function blurPx(screen, sx) {
    return S.blur * (screen.w / 1280) * sx;
}

/**
 * Кадр в холст ЛЮБОГО размера — превью, файл, кадры анимации идут отсюда.
 *  opts.preview  — зоны/макет и имитация дня/ночи (в файл не попадают)
 *  opts.v        — вид (для набора размеров — пересчитанный, не общий)
 *  opts.boot     — {alpha, scale} для кадров анимации
 *  opts.darken   — переопределить затемнение (замер читаемости идёт с 0)
 */
function paint(canvas, screen, opts) {
    const o = opts || {};
    const g = canvas.getContext('2d');
    const sx = canvas.width / screen.w;
    g.save();
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.clearRect(0, 0, canvas.width, canvas.height);
    g.scale(sx, canvas.height / screen.h);
    g.imageSmoothingQuality = 'high';

    if (S.mode === 'wall') paintWall(g, screen, sx, o);
    else paintLogo(g, screen, o);

    if (o.preview) {
        if (S.mode === 'wall' && S.overlay === 'zones') drawZones(g, screen);
        if (S.mode === 'wall' && S.overlay === 'ui') drawMockUi(g, screen);
        if (S.light === 'day') {
            // Солнце на экране — не «ярче», а белёсая пелена: контраст падает.
            g.fillStyle = 'rgba(255,255,255,0.24)';
            g.fillRect(0, 0, screen.w, screen.h);
        } else if (S.light === 'night') {
            g.fillStyle = 'rgba(0,0,0,0.5)';
            g.fillRect(0, 0, screen.w, screen.h);
        }
    }
    g.restore();
}

function paintWall(g, screen, sx, o) {
    const src = o.src || S.img || S.demo;
    const v = o.v || view();
    if (src && v) {
        const w = srcW(src) * v.scale, h = srcH(src) * v.scale;
        const b = blurPx(screen, sx);
        if (b > 0) {
            // ⚠️ Размытие тянет в кадр прозрачность из-за края холста: без
            // резкого слоя под ним у сохранённых обоев появлялась тёмная рамка
            // по периметру (JPEG кладёт прозрачное в чёрное). Резкий слой снизу
            // закрывает край, а размытый поверх даёт картинку.
            g.drawImage(src, v.x, v.y, w, h);
            g.filter = `blur(${b}px)`;
            g.drawImage(src, v.x, v.y, w, h);
            g.filter = 'none';
        } else {
            g.drawImage(src, v.x, v.y, w, h);
        }
    }
    const d = o.darken != null ? o.darken : S.darken;
    if (d > 0) {
        g.fillStyle = `rgba(0,0,0,${d / 100})`;
        g.fillRect(0, 0, screen.w, screen.h);
    }
}

function paintLogo(g, screen, o) {
    g.fillStyle = S.bg;
    g.fillRect(0, 0, screen.w, screen.h);
    const boot = o.boot || { alpha: 1, scale: 1 };
    g.save();
    g.globalAlpha = boot.alpha;
    if (boot.scale !== 1) {
        g.translate(screen.w / 2, screen.h / 2);
        g.scale(boot.scale, boot.scale);
        g.translate(-screen.w / 2, -screen.h / 2);
    }
    const v = o.v || view();
    let below = null;
    if (S.logo && v) {
        const w = S.logo.width * v.scale, h = S.logo.height * v.scale;
        g.drawImage(S.logo, v.x, v.y, w, h);
        below = { cx: v.x + w / 2, bottom: v.y + h };
    }
    const txt = S.text.trim();
    if (txt) {
        drawCaption(g, screen, txt, below);
    } else if (!S.logo && o.preview) {
        g.globalAlpha = boot.alpha * 0.28;
        drawCaption(g, screen, t('logo_placeholder'), null, '#ffffff');
    }
    g.restore();
}

function drawCaption(g, screen, raw, below, color) {
    const txt = S.spaced ? raw.toUpperCase() : raw;
    let size = screen.h * (S.textSize / 100);
    const font = (px) => `700 ${px}px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;
    const spacing = (px) => (S.spaced ? px * 0.22 : 0);
    const measure = (px) => {
        g.font = font(px);
        let w = 0;
        for (const ch of txt) w += g.measureText(ch).width;
        return w + spacing(px) * Math.max(0, [...txt].length - 1);
    };
    let w = measure(size);
    const maxW = screen.w * 0.9;
    if (w > maxW) { size *= maxW / w; w = measure(size); }
    const cx = below ? below.cx : screen.w / 2;
    let cy = below ? below.bottom + size * 1.05 : screen.h / 2;
    cy = Math.min(screen.h - size * 0.75, Math.max(size * 0.75, cy));
    g.font = font(size);
    g.fillStyle = color || S.textColor;
    g.textBaseline = 'middle';
    g.textAlign = 'left';
    let x = Math.min(screen.w - w - screen.w * 0.02, Math.max(screen.w * 0.02, cx - w / 2));
    for (const ch of txt) {
        g.fillText(ch, x, cy);
        x += g.measureText(ch).width + spacing(size);
    }
}

function drawZones(g, screen) {
    const W = screen.w, H = screen.h;
    const names = { status: t('zone_status'), dock: t('zone_dock'), widgets: t('zone_widgets') };
    g.save();
    g.lineWidth = Math.max(1, W / 400);
    for (const r of M.zoneRects(screen)) {
        g.fillStyle = 'rgba(96,165,250,0.22)';
        g.strokeStyle = 'rgba(96,165,250,0.9)';
        g.setLineDash([W / 90, W / 140]);
        g.fillRect(r.x, r.y, r.w, r.h);
        g.strokeRect(r.x, r.y, r.w, r.h);
        g.setLineDash([]);
        g.fillStyle = 'rgba(255,255,255,0.95)';
        g.font = `600 ${Math.round(Math.min(H * 0.045, r.h * 0.42))}px system-ui, sans-serif`;
        g.textBaseline = 'middle';
        const label = names[r.kind];
        const tw = g.measureText(label).width;
        if (tw < r.w * 0.9) g.fillText(label, r.x + (r.w - tw) / 2, r.y + r.h / 2);
    }
    const s = M.safeRect(screen);
    g.strokeStyle = 'rgba(74,222,128,0.95)';
    g.lineWidth = Math.max(1.5, W / 320);
    g.strokeRect(s.x, s.y, s.w, s.h);
    g.restore();
}

/**
 * Макет интерфейса: время и значки сверху, док снизу, виджет сбоку у широких.
 * Белым, с той же лёгкой тенью, что у настоящих лаунчеров, — чтобы человек
 * увидел глазами то, что меряет полоска читаемости.
 */
function drawMockUi(g, screen) {
    const W = screen.w, H = screen.h;
    const u = screen.ui || M.uiFor(W, H);
    g.save();
    g.fillStyle = '#fff'; g.strokeStyle = '#fff';
    g.shadowColor = 'rgba(0,0,0,0.35)'; g.shadowBlur = H * 0.006;
    const top = H * u.top, pad = W * 0.02;

    // строка состояния
    const fs = top * 0.42;
    g.font = `600 ${fs}px system-ui, sans-serif`;
    g.textBaseline = 'middle';
    g.fillText('12:45', pad + W * (u.left || 0), top / 2);
    let x = W - pad;
    const ic = fs * 0.9, cy = top / 2;
    for (let i = 0; i < 3; i++) {                                    // связь
        const bw = ic * 0.18, bh = ic * (0.35 + 0.22 * i);
        g.fillRect(x - (3 - i) * bw * 1.6, cy + ic / 2 - bh, bw, bh);
    }
    x -= ic * 1.4;
    g.lineWidth = Math.max(1, ic * 0.12);
    for (let i = 1; i <= 3; i++) {                                   // wi-fi
        g.beginPath(); g.arc(x - ic / 2, cy + ic * 0.45, ic * 0.22 * i, Math.PI * 1.25, Math.PI * 1.75); g.stroke();
    }
    x -= ic * 1.5;
    g.beginPath();                                                   // bluetooth
    g.moveTo(x - ic * 0.75, cy - ic * 0.25); g.lineTo(x - ic * 0.25, cy + ic * 0.25);
    g.lineTo(x - ic * 0.5, cy + ic * 0.5); g.lineTo(x - ic * 0.5, cy - ic * 0.5);
    g.lineTo(x - ic * 0.25, cy - ic * 0.25); g.lineTo(x - ic * 0.75, cy + ic * 0.25);
    g.stroke();

    // док
    const bh = H * u.bottom, by = H - bh;
    const size = bh * 0.56;
    const n = Math.max(4, Math.min(8, Math.floor((W * 0.8) / (size * 1.9))));
    const gap = (W * 0.84 - n * size) / (n - 1);
    let dx = W * 0.08;
    g.lineWidth = Math.max(1.2, size * 0.07);
    for (let i = 0; i < n; i++) {
        const y = by + (bh - size) / 2;
        roundRect(g, dx, y, size, size, size * 0.24);
        g.stroke();
        g.beginPath(); g.arc(dx + size / 2, y + size / 2, size * 0.16, 0, Math.PI * 2); g.fill();
        dx += size + gap;
    }

    // виджет часов у вытянутых экранов
    if (u.left > 0) {
        const wx = W * 0.02, wy = top + H * 0.03, ww = W * u.left - W * 0.03, wh = H * (1 - u.top - u.bottom) - H * 0.06;
        g.lineWidth = Math.max(1, W / 700);
        roundRect(g, wx, wy, ww, wh, ww * 0.08);
        g.stroke();
        g.font = `700 ${Math.min(ww * 0.28, wh * 0.3)}px system-ui, sans-serif`;
        g.textAlign = 'center';
        g.fillText('12:45', wx + ww / 2, wy + wh * 0.42);
        g.fillRect(wx + ww * 0.2, wy + wh * 0.68, ww * 0.6, Math.max(2, wh * 0.03));
        g.fillRect(wx + ww * 0.3, wy + wh * 0.78, ww * 0.4, Math.max(2, wh * 0.03));
    }
    g.restore();
}

function roundRect(g, x, y, w, h, r) {
    g.beginPath();
    g.moveTo(x + r, y); g.arcTo(x + w, y, x + w, y + h, r); g.arcTo(x + w, y + h, x, y + h, r);
    g.arcTo(x, y + h, x, y, r); g.arcTo(x, y, x + w, y, r); g.closePath();
}

/* ─────────────────────────────── превью на экране ───────────────────────── */

let ui = null;
let rafPending = false;

function requestDraw() {
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame(() => { rafPending = false; draw(); });
}

function draw() {
    const cv = ui.canvas;
    const box = ui.screenBox.getBoundingClientRect();
    const ratio = S.screen.h / S.screen.w;
    // Высота прибора держится в окне: экран не выше, чем остаётся от окна за
    // вычетом ВСЕГО остального в сцене (строка файла, панель, выдача, лимит) —
    // это «остальное» меряется, а не угадывается: оно зависит от языка,
    // переноса строк и того, показан ли блок лимита. Ширина — по контейнеру.
    const narrow = box.width < 560;
    const chrome = Math.max(0, ui.stagePanel.getBoundingClientRect().height - cv.getBoundingClientRect().height);
    const maxH = narrow ? window.innerHeight * 0.62 : Math.max(180, window.innerHeight - chrome - 56);
    let w = Math.max(160, box.width);
    if (w * ratio > maxH) w = maxH / ratio;
    const h = w * ratio;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    cv.style.width = Math.round(w) + 'px';
    cv.style.height = Math.round(h) + 'px';
    const pw = Math.round(w * dpr), ph = Math.round(h * dpr);
    if (cv.width !== pw) cv.width = pw;
    if (cv.height !== ph) cv.height = ph;

    let boot = null;
    if (S.bootPreview) {
        const el = (performance.now() - S.bootPreview.start) / 1000;
        const fade = Boot.FADE_SECONDS;
        boot = Boot.fadeFrame(Math.min(1, el / fade) * 29, 30);
        if (el < fade + 0.9) requestDraw();
        else S.bootPreview = null;
    }
    paint(cv, S.screen, { preview: true, boot });
    syncReadouts();
    scheduleLegibility();
}

function syncReadouts() {
    const s = S.screen;
    ui.dims.textContent = `${s.w} × ${s.h} · ${M.aspectLabel(s.w, s.h)}`;
    const src = source();
    const v = src ? view() : null;
    if (v) {
        let pct;
        if (S.mode === 'logo') pct = Math.round((Math.max(srcW(src), srcH(src)) * v.scale / Math.min(s.w, s.h)) * 100);
        else pct = Math.round(M.relativeZoom(v, s, srcW(src), srcH(src), 'cover') * 100);
        if (document.activeElement !== ui.zoom) ui.zoom.value = String(pct);
        ui.zoomVal.textContent = pct + '%';
    }
    ui.zoom.disabled = !v;
    ui.zoom.min = S.mode === 'logo' ? '5' : '100';
    ui.zoom.max = S.mode === 'logo' ? '100' : '400';

    const ready = S.mode === 'wall' ? !!S.img : !!(S.img || S.text.trim());
    ui.save.disabled = S.busy || !ready;
    ui.pack.disabled = S.busy || !ready;
    if (ui.share) ui.share.disabled = S.busy || !ready || S.fmt[S.mode] === 'anim';
    ui.needHint.hidden = ready;
    ui.needHint.textContent = t(S.mode === 'wall' ? 'need_photo' : 'need_logo');
    ui.demoTag.hidden = !(S.mode === 'wall' && !S.img);
}

/* ─────────────────────────────── читаемость ──────────────────────────────── */

let legTimer = 0;
function scheduleLegibility() {
    clearTimeout(legTimer);
    legTimer = setTimeout(measureLegibility, 140);
}

function sampleZones() {
    const s = S.screen;
    const k = Math.min(1, 360 / s.w);
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(s.w * k)); c.height = Math.max(1, Math.round(s.h * k));
    paint(c, s, { darken: 0 });
    const data = c.getContext('2d', { willReadFrequently: true }).getImageData(0, 0, c.width, c.height).data;
    const rects = M.zoneRects(s).map((r) => ({ x: r.x * k, y: r.y * k, w: r.w * k, h: r.h * k }));
    return { data, w: c.width, h: c.height, rects };
}

function measureLegibility() {
    const box = ui.leg;
    if (S.mode !== 'wall' || !(S.img || S.demo)) { box.hidden = true; return; }
    box.hidden = false;
    let share;
    try {
        const z = sampleZones();
        share = M.legibleShare(z.data, z.w, z.h, z.rects, S.darken / 100);
    } catch (e) { box.hidden = true; return; }
    const grade = M.legibilityGrade(share);
    const pct = Math.round(share * 100);
    ui.legBar.style.width = pct + '%';
    ui.leg.dataset.grade = grade;
    ui.legVal.textContent = t('leg_' + grade, { pct });
}

function autoLegibility() {
    if (S.mode !== 'wall') return;
    let r;
    try {
        const z = sampleZones();
        r = M.autoDarken(z.data, z.w, z.h, z.rects);
    } catch (e) { return; }
    ui.darken.value = String(r.darken);
    ui.darken.dispatchEvent(new Event('input', { bubbles: true }));
    ae('feature_use', { action: 'auto_darken', darken: r.darken, share: Math.round(r.share * 100), reached: r.reached ? 1 : 0 });
    if (!r.reached) toast(t('leg_not_reached'), 'info');
}

/* ────────────────────────────────── загрузка ─────────────────────────────── */

/**
 * ⚠️ Декодирование — в try/catch, и это не перестраховка: отказ `onerror`,
 * который никто не ловил, оставлял человека без единого слова (проверено
 * битым JPEG). И по типу файла заранее не отказываем: у .heic из проводника
 * Windows тип часто пустой, решает декодер.
 *
 * ⚠️ HEIC. На странице стояло «снимки HEIC принимаются наравне с JPG», а
 * загрузка шла через `<img>`, который HEIC открывает только в Safari: в Chrome
 * и Firefox фото с айфона отвергалось. Теперь HEIC сначала идёт через общий
 * `HeicDecode.normalize` (он есть на каждой странице с загрузкой).
 */
async function loadFile(file) {
    if (!file) return;
    const name = String(file.name || '');
    const looksImage = /^image\//.test(file.type || '') || /\.(jpe?g|png|webp|gif|bmp|avif|heic|heif|svg)$/i.test(name);
    if (!looksImage) {
        toast(t('err_not_image'), 'warning');
        ae('tool_error', { action: 'not_image', type: file.type || '', ext: (name.split('.').pop() || '').slice(0, 8) });
        return;
    }
    const heic = /\.(heic|heif)$/i.test(name) || /heic|heif/i.test(file.type || '');
    let usable = file;
    if (heic && window.HeicDecode && window.HeicDecode.normalize) {
        try { usable = await window.HeicDecode.normalize(file, window.__ae || null); }
        catch (e) { usable = file; }
    }
    const url = URL.createObjectURL(usable);
    let im;
    try {
        im = new Image();
        im.decoding = 'async';
        await new Promise((res, rej) => { im.onload = res; im.onerror = () => rej(new Error('decode')); im.src = url; });
        if (!im.naturalWidth || !im.naturalHeight) throw new Error('empty');
    } catch (e) {
        URL.revokeObjectURL(url);
        toast(t(heic ? 'err_heic' : 'err_broken'), 'error');
        stage('decode_failed', { heic: heic ? 1 : 0 });
        ae('tool_error', { action: 'decode_failed', heic: heic ? 1 : 0, type: file.type || '', bytes: file.size || 0, ext: (name.split('.').pop() || '').slice(0, 8) });
        return;
    }
    S.srcFile = file;
    S.img = im;
    // Ключ капа штампуется ОДИН раз по исходному файлу: подбор размера,
    // кадра и затемнения по одному снимку — одна единица, а не каждое нажатие.
    S.limitKey = `${name}|${file.size}|${file.lastModified || 0}`;
    S.views = { wall: null, logo: null };
    const field = looksLikeLightField(im);
    if (field !== S.keyOut) { S.keyOut = field; ui.keyOut.checked = field; }
    ui.keyHint.hidden = !field;
    rebuildLogo();
    requestDraw();
    stage('image_loaded', { w: im.naturalWidth, h: im.naturalHeight, mode: S.mode });
    ae('feature_use', { action: 'image_loaded', w: im.naturalWidth, h: im.naturalHeight, mode: S.mode, light_field: field ? 1 : 0 });
}

function clearFile() {
    S.img = null; S.srcFile = null; S.limitKey = null; S.logo = null;
    S.views = { wall: null, logo: null };
    ui.keyHint.hidden = true;
    requestDraw();
}

/* ──────────────────────────────── жесты на кадре ──────────────────────────── */

function wireCanvas() {
    const cv = ui.canvas;
    const pts = new Map();
    let base = null;

    const toFrame = (cx, cy) => {
        const r = cv.getBoundingClientRect();
        return { x: (cx - r.left) / r.width * S.screen.w, y: (cy - r.top) / r.height * S.screen.h };
    };
    const dims = () => { const s = source(); return s ? [srcW(s), srcH(s)] : null; };

    const snapshot = () => {
        const v = view();
        const arr = [...pts.values()];
        if (arr.length === 1) return { v: { ...v }, p: toFrame(arr[0].x, arr[0].y) };
        const a = toFrame(arr[0].x, arr[0].y), b = toFrame(arr[1].x, arr[1].y);
        return { v: { ...v }, mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, dist: Math.hypot(a.x - b.x, a.y - b.y) || 1 };
    };

    cv.addEventListener('pointerdown', (e) => {
        if (!source()) return;
        cv.setPointerCapture(e.pointerId);
        pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
        base = snapshot();
        cv.classList.add('is-drag');
    });
    cv.addEventListener('pointermove', (e) => {
        if (!pts.has(e.pointerId) || !base) return;
        pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
        const d = dims(); if (!d) return;
        const fit = fitOf(S.mode);
        const arr = [...pts.values()];
        if (arr.length === 1 && base.p) {
            const p = toFrame(arr[0].x, arr[0].y);
            setView(M.clampView({ scale: base.v.scale, x: base.v.x + p.x - base.p.x, y: base.v.y + p.y - base.p.y }, S.screen, d[0], d[1], fit));
        } else if (arr.length >= 2 && base.mid) {
            const a = toFrame(arr[0].x, arr[0].y), b = toFrame(arr[1].x, arr[1].y);
            const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
            const f = (Math.hypot(a.x - b.x, a.y - b.y) || 1) / base.dist;
            const moved = { scale: base.v.scale, x: base.v.x + mid.x - base.mid.x, y: base.v.y + mid.y - base.mid.y };
            setView(M.zoomAt(moved, f, mid.x, mid.y, S.screen, d[0], d[1], fit));
        }
        requestDraw();
    });
    const end = (e) => {
        if (!pts.delete(e.pointerId)) return;
        base = pts.size ? snapshot() : null;
        if (!pts.size) {
            cv.classList.remove('is-drag');
            ae('feature_use', { action: 'frame_adjust', mode: S.mode });
        }
    };
    cv.addEventListener('pointerup', end);
    cv.addEventListener('pointercancel', end);

    cv.addEventListener('wheel', (e) => {
        const d = dims(); if (!d) return;
        e.preventDefault();
        const p = toFrame(e.clientX, e.clientY);
        setView(M.zoomAt(view(), e.deltaY < 0 ? 1.08 : 1 / 1.08, p.x, p.y, S.screen, d[0], d[1], fitOf(S.mode)));
        requestDraw();
    }, { passive: false });

    cv.addEventListener('keydown', (e) => {
        const d = dims(); if (!d) return;
        const v = view(), fit = fitOf(S.mode);
        const step = (e.shiftKey ? 0.1 : 0.02) * S.screen.w;
        let nv = null;
        if (e.key === 'ArrowLeft') nv = { ...v, x: v.x - step };
        else if (e.key === 'ArrowRight') nv = { ...v, x: v.x + step };
        else if (e.key === 'ArrowUp') nv = { ...v, y: v.y - step };
        else if (e.key === 'ArrowDown') nv = { ...v, y: v.y + step };
        else if (e.key === '+' || e.key === '=') nv = M.zoomAt(v, 1.08, S.screen.w / 2, S.screen.h / 2, S.screen, d[0], d[1], fit);
        else if (e.key === '-' || e.key === '_') nv = M.zoomAt(v, 1 / 1.08, S.screen.w / 2, S.screen.h / 2, S.screen, d[0], d[1], fit);
        else if (e.key === '0' || e.key === 'Home') { resetView(); requestDraw(); e.preventDefault(); return; }
        if (!nv) return;
        e.preventDefault();
        setView(M.clampView(nv, S.screen, d[0], d[1], fit));
        requestDraw();
    });

    ui.zoom.addEventListener('input', () => {
        const src = source(); if (!src) return;
        const v = view(), s = S.screen, iw = srcW(src), ih = srcH(src);
        const val = Number(ui.zoom.value) / 100;
        let target;
        if (S.mode === 'logo') target = (val * Math.min(s.w, s.h)) / Math.max(iw, ih);
        else target = M.coverScale(s, iw, ih) * val;
        setView(M.zoomAt(v, target / v.scale, s.w / 2, s.h / 2, s, iw, ih, fitOf(S.mode)));
        requestDraw();
    });
}

/* ──────────────────────────────────── выдача ─────────────────────────────── */

function outCanvas(screen, boot) {
    const c = document.createElement('canvas');
    c.width = screen.w; c.height = screen.h;
    let v = null;
    const src = source();
    if (src && screen !== S.screen) v = M.retarget(view(), S.screen, screen, srcW(src), srcH(src), fitOf(S.mode));
    paint(c, screen, { v: v || undefined, boot, src: S.mode === 'wall' ? S.img : undefined });
    return c;
}

const toBlob = (c, type, q) => new Promise((r) => c.toBlob(r, type, q));

async function encode(c, fmt) {
    if (fmt === 'bmp') {
        const data = c.getContext('2d', { willReadFrequently: true }).getImageData(0, 0, c.width, c.height).data;
        return new Blob([encodeBmp24(data, c.width, c.height)], { type: 'image/bmp' });
    }
    if (fmt === 'png') return toBlob(c, 'image/png');
    return toBlob(c, 'image/jpeg', 0.92);
}

function baseName() {
    const src = ((S.srcFile && S.srcFile.name) || 'wallpaper').replace(/\.[^.]+$/, '');
    if (window.__outputName) return window.__outputName.sanitize(src, { maxLen: 48 }) || 'wallpaper';
    return src.replace(/[^\w\- ]+/g, '').slice(0, 48) || 'wallpaper';
}

function fileName(screen, fmt) {
    if (S.mode === 'logo') {
        // Прошивка ищет файл по ИМЕНИ: LOGO.bmp. Любое другое — «логотип не найден».
        if (fmt === 'bmp') return 'LOGO.bmp';
        return `logo ${screen.w}x${screen.h}.${fmt}`;
    }
    return `${baseName()} ${screen.w}x${screen.h}.${fmt}`;
}

function unitKey() {
    if (S.mode === 'logo' && !S.img) return 'logo-text:' + S.text.trim().toLowerCase();
    return S.limitKey || 'no-file';
}

function beginLimit() {
    if (!window.__toolLimit) return { commit() {}, abort() {} };
    return window.__toolLimit.begin(SLUG, [unitKey()]);
}

function setBusy(on, label) {
    S.busy = on;
    ui.save.classList.toggle('is-busy', !!on);
    ui.saveLabel.textContent = on && label ? label : saveLabel();
    syncReadouts();
}

function saveLabel() {
    const f = S.fmt[S.mode];
    if (f === 'anim') return t('btn_save_anim');
    if (S.mode === 'logo' && f === 'bmp') return t('btn_save_bmp');
    return t('btn_save_fmt', { fmt: f.toUpperCase() });
}

async function saveOne() {
    const fmt = S.fmt[S.mode];
    if (fmt === 'anim') return saveAnim();
    const run = beginLimit();
    if (!run) return;
    setBusy(true);
    try {
        const c = outCanvas(S.screen);
        const blob = await encode(c, fmt);
        if (!blob) throw new Error('encode');
        const name = fileName(S.screen, fmt);
        window.__downloadFile(blob, name);
        run.commit();
        ae('tool_complete', { action: 'export_one', mode: S.mode, fmt, screen: S.screen.id, device: S.device, bytes: blob.size });
        toast(t('saved_one', { name }), 'success');
    } catch (e) {
        run.abort();
        toast(t('err_export'), 'error');
        ae('tool_error', { action: 'export_failed', mode: S.mode, fmt, screen: S.screen.id, msg: String(e && e.message || e).slice(0, 80) });
    } finally {
        setBusy(false);
    }
}

async function shareOne() {
    const fmt = S.fmt[S.mode];
    if (fmt === 'anim' || !navigator.share) return;
    const run = beginLimit();
    if (!run) return;
    setBusy(true);
    try {
        const c = outCanvas(S.screen);
        const blob = await encode(c, fmt);
        const name = fileName(S.screen, fmt);
        const file = new File([blob], name, { type: blob.type });
        if (navigator.canShare && !navigator.canShare({ files: [file] })) throw new Error('cannot-share');
        await navigator.share({ files: [file], title: name });
        run.commit();
        ae('tool_complete', { action: 'share', mode: S.mode, fmt, screen: S.screen.id });
    } catch (e) {
        run.abort();
        if (!(e && e.name === 'AbortError')) toast(t('err_share'), 'warning');
    } finally {
        setBusy(false);
    }
}

/**
 * Набор размеров. Точное разрешение своей магнитолы знают не все, поэтому
 * «сделать все ходовые» — равноправная кнопка.
 *
 *  обои    — файлы по одному через __batchDeliver (папка → архив): браузер даёт
 *            ОДИН файл на жест, цикл по __downloadFile потерял бы остальные;
 *  логотип — ОДИН архив с папками «1280x720/LOGO.bmp»: прошивка ищет файл по
 *            точному имени, и шесть файлов с одним именем в одной папке
 *            перезаписали бы друг друга.
 *
 * ⚠️ Вид для каждого размера считается КОПИЕЙ (M.retarget) — общий вид, в котором
 * человек подогнал кадр, не трогается. Раньше трогался.
 */
async function savePack() {
    const fmt = S.fmt[S.mode] === 'anim' ? 'bmp' : S.fmt[S.mode];
    const run = beginLimit();
    if (!run) return;
    const sizes = M.PACK.map(M.screenById);
    if (S.mode === 'logo') {
        setBusy(true, t('busy_pack'));
        try {
            const files = [];
            for (const s of sizes) {
                const blob = await encode(outCanvas(s), fmt);
                const f = new File([blob], fileName(s, fmt), { type: blob.type });
                f.path = `${s.w}x${s.h}/${fileName(s, fmt)}`;
                files.push(f);
                await yieldTask();
            }
            const zip = await window.__FileTransfer.FolderZip.wrap(files, { name: 'logo.zip' });
            window.__downloadFile(zip.slice(0, zip.size), 'car-logo-all-sizes.zip');
            run.commit();
            ae('tool_complete', { action: 'export_all', mode: 'logo', fmt, n: sizes.length });
            toast(t('saved_all', { n: sizes.length }), 'success');
        } catch (e) {
            run.abort();
            window.__handleError ? window.__handleError(e, SLUG, { say: t('err_export') }) : toast(t('err_export'), 'error');
        } finally { setBusy(false); }
        return;
    }
    try {
        const batch = await window.__batchDeliver.openBatch({ zipName: baseName() + ' wallpapers.zip', slug: SLUG, total: sizes.length });
        setBusy(true, t('busy_pack'));
        for (const s of sizes) {
            const blob = await encode(outCanvas(s), fmt);
            if (blob) await batch.write(fileName(s, fmt), blob);
        }
        const res = await batch.finish();
        run.commit();
        ae('tool_complete', { action: 'export_all', mode: 'wall', fmt, n: sizes.length, via: (res && res.via) || batch.mode });
        toast(t('saved_all', { n: sizes.length }), 'success');
    } catch (e) {
        run.abort();
        window.__handleError ? window.__handleError(e, SLUG, { say: t('err_export') }) : toast(t('err_export'), 'error');
    } finally { setBusy(false); }
}

function yieldTask() {
    if (window.scheduler && window.scheduler.yield) return window.scheduler.yield();
    return new Promise((r) => setTimeout(r, 0));
}

/** bootanimation.zip — перк Премиума. Не-Премиум видит предложение, а не молчание. */
async function saveAnim() {
    if (!isPro()) { offerPro(); return; }
    if (S.screen.w * S.screen.h > 2560 * 1600) { toast(t('err_export'), 'error'); return; }
    const run = beginLimit();
    if (!run) return;
    setBusy(true, t('busy_frames', { n: 0 }));
    try {
        const blob = await Boot.build(S.screen.w, S.screen.h,
            async (state) => {
                const b = await toBlob(outCanvas(S.screen, state), 'image/png');
                await yieldTask();
                return b;
            },
            (files, opts) => window.__FileTransfer.FolderZip.wrap(files, opts),
            (done, total) => { ui.saveLabel.textContent = t('busy_frames', { n: Math.round((done / total) * 100) }); });
        window.__downloadFile(blob, 'bootanimation.zip');
        run.commit();
        ae('tool_complete', { action: 'export_anim', screen: S.screen.id, device: S.device, bytes: blob.size });
        toast(t('saved_one', { name: 'bootanimation.zip' }), 'success');
    } catch (e) {
        run.abort();
        window.__handleError ? window.__handleError(e, SLUG, { say: t('err_export') }) : toast(t('err_export'), 'error');
    } finally { setBusy(false); }
}

function offerPro() {
    ae('pro_offer', { what: 'boot_animation' });
    ui.proBox.hidden = false;
    ui.proBox.scrollIntoView({ block: 'nearest' });
}

/* ─────────────────────────────── органы управления ─────────────────────────── */

const FORMATS = { wall: ['jpg', 'png'], logo: ['bmp', 'jpg', 'png', 'anim'] };

function renderFormats() {
    const cur = S.fmt[S.mode];
    ui.fmt.innerHTML = FORMATS[S.mode].map((f) => {
        const label = f === 'anim' ? t('fmt_anim') : (f === 'bmp' ? 'BMP' : f.toUpperCase());
        const star = f === 'anim' && !isPro() ? ' <span class="cw-star" aria-hidden="true">★</span>' : '';
        return `<button type="button" class="tc-seg-b${f === cur ? ' on' : ''}" data-fmt="${f}" aria-pressed="${f === cur}">${label}${star}</button>`;
    }).join('');
    ui.saveLabel.textContent = saveLabel();
    ui.animNote.hidden = !(S.mode === 'logo' && cur === 'anim');
    ui.replay.hidden = !(S.mode === 'logo' && cur === 'anim');
    ui.pack.hidden = cur === 'anim';
    if (cur !== 'anim' || isPro()) ui.proBox.hidden = true;
    syncFmtNote();
}

function syncFmtNote() {
    const f = S.fmt[S.mode];
    const dev = M.deviceById(S.device.slice(2));
    let key = S.mode === 'wall' ? 'fmtnote_wall_' + f : 'fmtnote_logo_' + f;
    if (S.mode === 'logo' && f === 'anim' && dev && dev.platform === 'teyes2k') key = 'fmtnote_anim_locked';
    ui.fmtNote.textContent = t(key, { w: S.screen.w, h: S.screen.h });
    ui.fmtNote.classList.toggle('warn', key === 'fmtnote_anim_locked');
}

function setMode(mode, userAction) {
    if (mode === S.mode) return;
    const apply = () => {
        S.mode = mode;
        ui.segMode.querySelectorAll('[data-mode]').forEach((b) => {
            const on = b.dataset.mode === mode;
            b.classList.toggle('on', on);
            b.setAttribute('aria-selected', String(on));
        });
        ui.panelWall.hidden = mode !== 'wall';
        ui.panelLogo.hidden = mode !== 'logo';
        ui.overlayGroup.hidden = mode !== 'wall';
        ui.installWall.hidden = mode !== 'wall';
        ui.installLogo.hidden = mode !== 'logo';
        ui.app.dataset.mode = mode;
        renderFormats();
        draw();
    };
    if (userAction && window.__viewTransition) window.__viewTransition(apply); else apply();
    if (userAction) {
        settings.save('mode', mode);
        ae('feature_use', { action: 'mode', mode });
    }
}

function applyDevice(value, userAction) {
    let next = null;
    if (value === 'custom') {
        ui.custom.hidden = false;
        next = M.customScreen(ui.cw.value, ui.ch.value);
        ui.cw.classList.toggle('is-bad', !next);
        ui.ch.classList.toggle('is-bad', !next);
        if (!next) return;
    } else {
        ui.custom.hidden = true;
        if (value.startsWith('d:')) { const d = M.deviceById(value.slice(2)); next = d && M.screenById(d.screen); }
        else if (value.startsWith('s:')) next = M.screenById(value.slice(2));
    }
    if (!next) return;
    S.device = value;
    setScreen(next);
    syncFmtNote();
    requestDraw();
    if (userAction) {
        settings.save('device', value);
        if (value === 'custom') { settings.save('customW', ui.cw.value); settings.save('customH', ui.ch.value); }
        ae('feature_use', { action: 'device', device: value, screen: next.id });
    }
}

function buildDeviceSelect() {
    const opt = (value, text) => `<option value="${value}">${text}</option>`;
    const res = (id) => { const s = M.screenById(id); return `${s.w}×${s.h}`; };
    const groups = [
        ['teyes', 'grp_teyes'], ['android', 'grp_android'], ['oem', 'grp_oem'],
    ];
    let html = '';
    for (const [g, key] of groups) {
        html += `<optgroup label="${t(key)}">`;
        for (const d of M.DEVICES.filter((x) => x.group === g)) {
            html += opt('d:' + d.id, `${d.name || t(d.label)} — ${res(d.screen)}`);
        }
        html += '</optgroup>';
    }
    html += `<optgroup label="${t('grp_resolution')}">`;
    for (const s of M.SCREENS) html += opt('s:' + s.id, `${s.w} × ${s.h}`);
    html += opt('custom', t('opt_custom')) + '</optgroup>';
    ui.device.innerHTML = html;
}

function bindRange(el, key, out, fmt) {
    const saved = Number(settings.load(key, S[key]));
    if (Number.isFinite(saved)) el.value = String(saved);
    const sync = (user) => {
        S[key] = Number(el.value) || 0;
        if (out) out.textContent = fmt(S[key]);
        if (user) settings.save(key, S[key]);
        requestDraw();
    };
    el.addEventListener('input', () => sync(true));
    el.addEventListener('change', () => ae('feature_use', { action: key, value: S[key] }));
    sync(false);
}

function bindSeg(root, attr, key, onChange) {
    root.addEventListener('click', (e) => {
        const b = e.target.closest(`[data-${attr}]`);
        if (!b) return;
        const val = b.dataset[attr];
        root.querySelectorAll(`[data-${attr}]`).forEach((x) => {
            x.classList.toggle('on', x === b);
            x.setAttribute('aria-pressed', String(x === b));
        });
        onChange(val, true);
    });
    const saved = settings.load(key, null);
    if (saved) {
        const b = root.querySelector(`[data-${attr}="${saved}"]`);
        if (b) { root.querySelectorAll(`[data-${attr}]`).forEach((x) => { x.classList.toggle('on', x === b); x.setAttribute('aria-pressed', String(x === b)); }); onChange(saved, false); }
    }
}

export function mount(cfg) {
    L = cfg.lang || {};
    ui = {
        app: $('cwApp'), stagePanel: $('cwStagePanel'), canvas: $('cwCanvas'), screenBox: $('cwScreen'), dims: $('cwDims'), demoTag: $('cwDemo'),
        segMode: $('cwMode'), panelWall: $('cwPanelWall'), panelLogo: $('cwPanelLogo'),
        device: $('cwDevice'), custom: $('cwCustom'), cw: $('cwCustomW'), ch: $('cwCustomH'),
        darken: $('cwDarken'), blur: $('cwBlur'),
        leg: $('cwLeg'), legBar: $('cwLegBar'), legVal: $('cwLegVal'),
        overlayGroup: $('cwOverlay'), light: $('cwLight'), zoom: $('cwZoom'), zoomVal: $('cwZoomVal'),
        fmt: $('cwFmt'), fmtNote: $('cwFmtNote'), save: $('cwSave'), saveLabel: $('cwSaveLabel'), pack: $('cwPack'),
        share: $('cwShare'), needHint: $('cwNeed'), proBox: $('cwProBox'), animNote: $('cwAnimNote'), replay: $('cwReplay'),
        keyOut: $('cwKeyOut'), keyHint: $('cwKeyHint'), tintWhite: $('cwTint'), bg: $('cwBg'), eyedrop: $('cwEyedrop'),
        text: $('cwText'), textSize: $('cwTextSize'), textColor: $('cwTextColor'),
        installWall: $('cwInstallWall'), installLogo: $('cwInstallLogo'),
    };

    S.demo = makeDemo();
    buildDeviceSelect();

    /* магнитола */
    const savedDevice = settings.load('device', S.device);
    ui.cw.value = settings.load('customW', '1280');
    ui.ch.value = settings.load('customH', '720');
    ui.device.value = [...ui.device.options].some((o) => o.value === savedDevice) ? savedDevice : S.device;
    applyDevice(ui.device.value, false);
    ui.device.addEventListener('change', () => applyDevice(ui.device.value, true));
    [ui.cw, ui.ch].forEach((el) => el.addEventListener('input', () => { if (ui.device.value === 'custom') applyDevice('custom', true); }));

    /* обои */
    bindRange(ui.darken, 'darken', $('cwDarkenVal'), (v) => v + '%');
    bindRange(ui.blur, 'blur', $('cwBlurVal'), (v) => String(v));
    $('cwAuto').addEventListener('click', autoLegibility);

    // Прежний чекбокс «показывать зоны» (ключ showUi) переезжает в переключатель.
    const legacyShowUi = settings.load('showUi', null);
    if (legacyShowUi === false || legacyShowUi === 'false' || legacyShowUi === 0) S.overlay = 'none';
    bindSeg(ui.overlayGroup, 'overlay', 'overlay', (v, user) => {
        S.overlay = v; requestDraw();
        if (user) { settings.save('overlay', v); ae('feature_use', { action: 'overlay', overlay: v }); }
    });
    if (S.overlay === 'none' && !settings.load('overlay', null)) {
        ui.overlayGroup.querySelectorAll('[data-overlay]').forEach((x) => { const on = x.dataset.overlay === 'none'; x.classList.toggle('on', on); x.setAttribute('aria-pressed', String(on)); });
    }
    bindSeg(ui.light, 'light', 'light', (v, user) => {
        S.light = v; requestDraw();
        if (user) { settings.save('light', v); ae('feature_use', { action: 'light', light: v }); }
    });

    /* логотип */
    const savedBg = settings.load('bg', S.bg);
    if (/^#[0-9a-f]{6}$/i.test(savedBg)) S.bg = savedBg;
    ui.bg.value = S.bg;
    ui.bg.addEventListener('input', () => { S.bg = ui.bg.value; settings.save('bg', S.bg); syncSwatches(); requestDraw(); });
    const syncSwatches = () => document.querySelectorAll('[data-bg]').forEach((b) => b.classList.toggle('on', b.dataset.bg.toLowerCase() === S.bg.toLowerCase()));
    document.querySelectorAll('[data-bg]').forEach((b) => b.addEventListener('click', () => { S.bg = b.dataset.bg; ui.bg.value = S.bg; settings.save('bg', S.bg); syncSwatches(); requestDraw(); }));
    syncSwatches();
    // EyeDropper — взять фирменный цвет прямо с логотипа на экране. Только там,
    // где API есть (Chromium на компьютере); иначе кнопки нет вовсе.
    if ('EyeDropper' in window) {
        ui.eyedrop.hidden = false;
        ui.eyedrop.addEventListener('click', async () => {
            try {
                const r = await new window.EyeDropper().open();
                if (r && r.sRGBHex) { S.bg = r.sRGBHex; ui.bg.value = S.bg; settings.save('bg', S.bg); syncSwatches(); requestDraw(); ae('feature_use', { action: 'eyedropper' }); }
            } catch (e) { /* отмена */ }
        });
    }
    ui.keyOut.addEventListener('change', () => { S.keyOut = ui.keyOut.checked; rebuildLogo(); requestDraw(); ae('feature_use', { action: 'key_out', on: S.keyOut ? 1 : 0 }); });
    S.tintWhite = !!settings.load('tintWhite', false);
    ui.tintWhite.checked = S.tintWhite;
    ui.tintWhite.addEventListener('change', () => { S.tintWhite = ui.tintWhite.checked; settings.save('tintWhite', S.tintWhite); rebuildLogo(); requestDraw(); });
    ui.text.value = settings.load('text', '');
    S.text = ui.text.value;
    ui.text.addEventListener('input', () => { S.text = ui.text.value; settings.save('text', S.text); requestDraw(); });
    bindRange(ui.textSize, 'textSize', $('cwTextSizeVal'), (v) => v + '%');
    const savedTc = settings.load('textColor', S.textColor);
    if (/^#[0-9a-f]{6}$/i.test(savedTc)) S.textColor = savedTc;
    ui.textColor.value = S.textColor;
    ui.textColor.addEventListener('input', () => { S.textColor = ui.textColor.value; settings.save('textColor', S.textColor); requestDraw(); });
    bindSeg($('cwSpacing'), 'spaced', 'spaced', (v, user) => { S.spaced = v === '1'; requestDraw(); if (user) settings.save('spaced', v); });

    /* выдача */
    const savedFmtWall = settings.load('fmtWall', 'jpg');
    const savedFmtLogo = settings.load('fmtLogo', 'bmp');
    if (FORMATS.wall.includes(savedFmtWall)) S.fmt.wall = savedFmtWall;
    if (FORMATS.logo.includes(savedFmtLogo) && savedFmtLogo !== 'anim') S.fmt.logo = savedFmtLogo;
    ui.fmt.addEventListener('click', (e) => {
        const b = e.target.closest('[data-fmt]');
        if (!b) return;
        S.fmt[S.mode] = b.dataset.fmt;
        if (b.dataset.fmt !== 'anim') settings.save(S.mode === 'wall' ? 'fmtWall' : 'fmtLogo', b.dataset.fmt);
        renderFormats();
        ae('feature_use', { action: 'format', mode: S.mode, fmt: b.dataset.fmt, pro: isPro() ? 1 : 0 });
        if (b.dataset.fmt === 'anim') playBoot();
    });
    ui.replay.addEventListener('click', playBoot);
    ui.save.addEventListener('click', saveOne);
    ui.pack.addEventListener('click', savePack);
    if (ui.share && navigator.share && navigator.canShare) {
        try {
            if (navigator.canShare({ files: [new File(['x'], 'a.jpg', { type: 'image/jpeg' })] })) {
                ui.share.hidden = false;
                ui.share.addEventListener('click', shareOne);
            }
        } catch (e) { /* нет — кнопки нет */ }
    }
    $('cwFit').addEventListener('click', () => { resetView(); requestDraw(); ae('feature_use', { action: 'fit' }); });

    /* режим */
    ui.segMode.addEventListener('click', (e) => { const b = e.target.closest('[data-mode]'); if (b) setMode(b.dataset.mode, true); });
    const savedMode = settings.load('mode', 'wall');
    S.mode = 'wall';
    if (savedMode === 'logo') setMode('logo', false); else { renderFormats(); }

    /* файл */
    const zone = $('cwUpload');
    if (zone) {
        window.__bindUploaderFile(zone, (file) => loadFile(file));
        zone.addEventListener('tool-uploader:clear', clearFile);
    }

    wireCanvas();
    if (window.ResizeObserver) new ResizeObserver(() => requestDraw()).observe(ui.screenBox);
    else window.addEventListener('resize', requestDraw);
    window.addEventListener('resize', requestDraw);
    document.addEventListener('tier-bootstrap:ready', () => renderFormats());
    draw();
}

function playBoot() {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    S.bootPreview = { start: performance.now() };
    requestDraw();
}

function boot() {
    const cfg = window.__CAR_WALL_CFG;
    if (!cfg || !document.getElementById('cwApp')) return;
    try { mount(cfg); } catch (e) {
        window.__handleError ? window.__handleError(e, SLUG) : console.error(e);
    }
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
